import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Typography,
  type AlertColor,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import { telegram } from './telegram';
import { isGpsUnsupportedMessage, formatGpsUnsupportedAccess } from './gpsUnsupportedAccess';
import {
  formatTelegramRequiredAccess,
  isTelegramRequiredError,
  isTelegramRequiredMessage,
  openZiaWellNavTelegram,
  type TelegramLaunchFeature,
} from './telegramRequiredAccess';

export type AppFeedbackSeverity = AlertColor;

export interface AppFeedbackNotice {
  title?: string;
  message: string;
  severity?: AppFeedbackSeverity;
  durationMs?: number;
  actionLabel?: string;
  actionUrl?: string;
  feature?: TelegramLaunchFeature;
}

export interface AppConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  severity?: Exclude<AppFeedbackSeverity, 'success'>;
}

type AppFeedbackEvent =
  | { type: 'toast'; notice: AppFeedbackNotice }
  | { type: 'confirm'; options: AppConfirmOptions; resolve: (confirmed: boolean) => void };

let feedbackListener: ((event: AppFeedbackEvent) => void) | null = null;

function messageFrom(value: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (value instanceof Error && value.message.trim()) return value.message.trim();
  if (value && typeof value === 'object') {
    const objectValue = value as { message?: unknown; error?: unknown; reason?: unknown };
    for (const candidate of [objectValue.message, objectValue.error, objectValue.reason]) {
      if (typeof candidate === 'string' && candidate.trim()) return candidate.trim();
    }
  }
  return fallback;
}

function severityHaptic(severity: AppFeedbackSeverity = 'info') {
  if (severity === 'success') telegram.hapticFeedback('success');
  else if (severity === 'error') telegram.hapticFeedback('error');
  else if (severity === 'warning') telegram.hapticFeedback('medium');
  else telegram.hapticFeedback('light');
}

export function normalizeAppError(
  error: unknown,
  fallbackMessage = 'Something went wrong. Please try again.',
  feature?: TelegramLaunchFeature,
): AppFeedbackNotice {
  if (isGpsUnsupportedMessage(error)) {
    const copy = formatGpsUnsupportedAccess(error);
    return {
      title: copy.title,
      message: copy.message,
      severity: 'warning',
      actionLabel: copy.actionLabel,
      actionUrl: copy.url,
      feature: 'gps',
    };
  }

  if (
    isTelegramRequiredError(error) ||
    isTelegramRequiredMessage(error) ||
    (error instanceof Error && isTelegramRequiredMessage(error.message))
  ) {
    const copy = formatTelegramRequiredAccess(error, feature);
    return {
      title: copy.title,
      message: copy.message,
      severity: 'info',
      actionLabel: copy.actionLabel,
      actionUrl: copy.url,
      feature: copy.feature,
    };
  }

  const message = messageFrom(error, fallbackMessage);
  const severity: AppFeedbackSeverity = /failed|unable|error|denied|invalid/i.test(message) ? 'error' : 'info';
  return { message, severity };
}

export function showToast(input: string | AppFeedbackNotice, severity: AppFeedbackSeverity = 'info') {
  const notice = typeof input === 'string' ? { message: input, severity } : { severity: 'info' as AppFeedbackSeverity, ...input };
  severityHaptic(notice.severity);

  if (feedbackListener) {
    feedbackListener({ type: 'toast', notice });
    return;
  }

  telegram.showAlert(notice.title ? `${notice.title}\n\n${notice.message}` : notice.message);
}

export function showActionNotice(input: AppFeedbackNotice) {
  showToast(input);
}

export function showErrorNotice(error: unknown, fallbackMessage?: string, feature?: TelegramLaunchFeature) {
  showActionNotice(normalizeAppError(error, fallbackMessage, feature));
}

export function showConfirm(input: string | AppConfirmOptions): Promise<boolean> {
  const options: AppConfirmOptions = typeof input === 'string'
    ? { message: input }
    : input;
  telegram.hapticFeedback(options.severity === 'error' ? 'error' : 'medium');

  if (feedbackListener) {
    const listener = feedbackListener;
    return new Promise((resolve) => {
      listener({ type: 'confirm', options, resolve });
    });
  }

  return new Promise((resolve) => {
    telegram.showConfirm(options.message, resolve);
  });
}

export function AppFeedbackHost() {
  const [notice, setNotice] = useState<AppFeedbackNotice | null>(null);
  const [confirmState, setConfirmState] = useState<{
    options: AppConfirmOptions;
    resolve: (confirmed: boolean) => void;
  } | null>(null);

  useEffect(() => {
    feedbackListener = (event) => {
      if (event.type === 'toast') {
        setNotice(event.notice);
      } else {
        setConfirmState({ options: event.options, resolve: event.resolve });
      }
    };

    return () => {
      feedbackListener = null;
    };
  }, []);

  const closeConfirm = (confirmed: boolean) => {
    const current = confirmState;
    setConfirmState(null);
    current?.resolve(confirmed);
  };

  const action = notice?.actionUrl ? (
    <Button
      color="inherit"
      size="small"
      startIcon={<OpenInNewIcon />}
      onClick={() => {
        if (notice.actionUrl) openZiaWellNavTelegram(notice.actionUrl);
      }}
      sx={{ fontWeight: 900, whiteSpace: 'nowrap' }}
    >
      {notice.actionLabel || 'Open'}
    </Button>
  ) : undefined;

  return (
    <>
      <Snackbar
        open={Boolean(notice)}
        autoHideDuration={notice?.durationMs ?? 5200}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 18px)' }}
      >
        {notice ? (
          <Alert
            severity={notice.severity || 'info'}
            onClose={() => setNotice(null)}
            action={action}
            sx={{
              maxWidth: 680,
              alignItems: notice.title ? 'flex-start' : 'center',
              borderRadius: 2,
              border: '1px solid rgba(34,211,238,0.26)',
              bgcolor: 'rgba(15,23,42,0.94)',
              color: '#e2e8f0',
              boxShadow: '0 18px 46px rgba(2,6,23,0.35)',
              '& .MuiAlert-icon': { color: notice.severity === 'error' ? '#f87171' : '#67e8f9' },
            }}
          >
            {notice.title ? (
              <Box sx={{ display: 'grid', gap: 0.35 }}>
                <Typography sx={{ fontWeight: 900, fontSize: '0.9rem', color: 'inherit' }}>
                  {notice.title}
                </Typography>
                <Typography sx={{ fontSize: '0.82rem', color: 'inherit' }}>
                  {notice.message}
                </Typography>
              </Box>
            ) : notice.message}
          </Alert>
        ) : undefined}
      </Snackbar>

      <Dialog
        open={Boolean(confirmState)}
        onClose={(_event, reason) => {
          if (reason === 'backdropClick') return;
          closeConfirm(false);
        }}
        PaperProps={{
          sx: {
            width: 'min(92vw, 420px)',
            borderRadius: 3,
            bgcolor: 'rgba(15,23,42,0.98)',
            color: '#e2e8f0',
            border: '1px solid rgba(34,211,238,0.24)',
            boxShadow: '0 28px 90px rgba(2,6,23,0.55)',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 950, color: '#67e8f9', pb: 1 }}>
          {confirmState?.options.title || 'Confirm action'}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#cbd5e1', fontSize: '0.95rem' }}>
            {confirmState?.options.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => closeConfirm(false)} sx={{ color: '#94a3b8', fontWeight: 800 }}>
            {confirmState?.options.cancelLabel || 'Cancel'}
          </Button>
          <Button
            variant="contained"
            onClick={() => closeConfirm(true)}
            color={confirmState?.options.severity === 'error' ? 'error' : 'primary'}
            sx={{ fontWeight: 900, textTransform: 'none' }}
          >
            {confirmState?.options.confirmLabel || 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
